import { Label } from 'components/indicators/label'

import { toast } from '@lib/toasts'
import { faCopy } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { Button } from 'components/buttons'
import { twJoin } from 'tailwind-merge'
import { useOnClickOutside } from '@hooks/useOnClickOutside'
import { useState, useRef, type ElementType } from 'react'
import { useTranslation } from 'react-i18next'

interface ApiStatusCellProps {
  /** The API URL to display and copy */
  apiUrl?: string
  /** Whether the API is currently active */
  isActive: boolean
  /** The name of the report */
  reportName: string
  /** Label text to show when API is active */
  activeLabel: string
  /** Label text to show when API is inactive */
  inactiveLabel: string
}

/**
 * Dropdown component that displays the API URL and copy button
 */
const ApiUrlDropdown = ({
  reportName,
  apiUrl,
  onCopy,
  onClose,
  copyButtonText,
}: {
  reportName: string
  apiUrl: string
  onCopy: () => Promise<void>
  onClose: () => void
  copyButtonText: string
}) => {
  const ref = useRef<HTMLDivElement>(null)

  useOnClickOutside(ref, onClose)

  return (
    <div
      ref={ref}
      className={twJoin(
        'absolute',
        'bg-neutral-50',
        'drop-shadow-md',
        'flex',
        'flex-col',
        'items-start',
        'min-w-64',
        'p-3',
        'right-0',
        'rounded',
        'text-neutral-900',
        'top-[100%]',
        'z-30',
      )}
    >
      <p className="font-semibold">{reportName}</p>
      <code className="break-all">{apiUrl}</code>
      <Button
        className="text-primary-600 px-0"
        variant="text"
        onClick={() => void onCopy()}
      >
        <FontAwesomeIcon icon={faCopy} aria-hidden="true" />
        {copyButtonText}
      </Button>
    </div>
  )
}

/**
 * Cell component that displays API status and provides access to the API URL
 */
export const ApiStatusCell = ({
  apiUrl,
  isActive,
  reportName,
  activeLabel,
  inactiveLabel,
}: ApiStatusCellProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const { t } = useTranslation()

  const copyToClipboard = async () => {
    if (!apiUrl) return

    try {
      await navigator.clipboard.writeText(apiUrl)
      toast({
        title: t('features.reporting.enableApiAccess.copiedUrl'),
        content: t('features.reporting.enableApiAccess.urlIsCopiedToClipboard'),
        variant: 'success',
      })
    } catch (error) {
      toast({
        title: t('common.error'),
        content: t('features.reporting.enableApiAccess.errorCopyingUrl'),
        variant: 'error',
      })
    }
  }

  const LabelWrapper: ElementType = isActive ? 'button' : 'div'

  return (
    <div
      className="relative"
      onMouseEnter={() => isActive && setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <LabelWrapper className={twJoin('w-full', isActive && 'cursor-pointer')}>
        <Label color={isActive ? 'success' : 'neutral'}>
          {isActive ? activeLabel : inactiveLabel}
        </Label>
      </LabelWrapper>
      {isOpen && isActive && apiUrl && (
        <ApiUrlDropdown
          reportName={reportName}
          apiUrl={apiUrl}
          onCopy={copyToClipboard}
          onClose={() => setIsOpen(false)}
          copyButtonText={t('features.reporting.enableApiAccess.copyUrl')}
        />
      )}
    </div>
  )
}
